import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, FileText, ShieldCheck, Mail } from "lucide-react";

export default function PublicProposal() {
  const { token } = useParams();
  const [p, setP] = useState(undefined); // undefined=loading, null=not found
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null); // "accepted" | "rejected"
  const [claimNeeded, setClaimNeeded] = useState(false);

  useEffect(() => {
    api.get(`/proposals/shared/${token}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [token]);

  const decide = async (decision) => {
    if (decision === "accepted" && !window.confirm("Approve this proposal? We'll email you a secure link to set up your portal and pay your deposit.")) return;
    setBusy(decision);
    try {
      const { data } = await api.post(`/proposals/shared/${token}/accept`, { decision });
      setDone(decision);
      setClaimNeeded(!!data.claim_needed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(""); }
  };

  if (p === undefined) return <div className="min-h-screen bg-navy flex items-center justify-center text-gray-400"><Loader2 className="animate-spin mr-2" size={18} /> Loading your proposal…</div>;
  if (p === null) return <div className="min-h-screen bg-navy flex items-center justify-center text-gray-400" data-testid="proposal-not-found">This proposal link isn't valid or has expired.</div>;

  const decided = done || (p.status === "accepted" ? "accepted" : p.status === "rejected" ? "rejected" : null);

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="public-proposal">
      <Helmet><title>{`${p.title || "Your Proposal"} | ATF WebWorks`}</title></Helmet>
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-2">
          <span className="font-heading font-bold tracking-wide">ATF <span className="text-gold">WebWorks</span></span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 text-gold px-3 py-1 text-xs uppercase tracking-widest mb-5">
          <FileText size={13} /> Website Proposal
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold">{p.organization || "Your Website Proposal"}</h1>
        <p className="text-gray-300 mt-2">
          {p.recommended_package}{p.price_range ? ` · ${p.price_range}` : ""}{p.timeline ? ` · ${p.timeline}` : ""}
        </p>

        {p.veteran_discount_percent > 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gold border border-gold/30 bg-gold/5 px-3 py-2">
            <ShieldCheck size={15} /> {Math.round(p.veteran_discount_percent)}% veteran appreciation discount applied — thank you for your service.
          </div>
        )}

        <div className="mt-8 bg-navy-800 border border-white/10 p-6 sm:p-8">
          <pre className="whitespace-pre-wrap font-body text-[15px] leading-relaxed text-gray-200" data-testid="proposal-body">{p.proposal_text}</pre>
        </div>

        {/* Decision */}
        <div className="mt-8">
          {decided === "accepted" ? (
            <div className="bg-green-500/10 border border-green-500/30 p-6 text-center" data-testid="proposal-accepted">
              <Check className="mx-auto text-green-400 mb-2" size={28} />
              <div className="text-lg font-semibold">Proposal approved — thank you!</div>
              <p className="text-gray-300 text-sm mt-2 flex items-center justify-center gap-2">
                <Mail size={15} className="text-gold" />
                {claimNeeded
                  ? "We've emailed you a secure link to set up your portal and pay your deposit to reserve your slot."
                  : "Check your email for next steps — your portal has your deposit ready to pay."}
              </p>
            </div>
          ) : decided === "rejected" ? (
            <div className="bg-navy-800 border border-white/10 p-6 text-center text-gray-300" data-testid="proposal-rejected">
              Thanks for the feedback — we'll be in touch to adjust the proposal.
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => decide("accepted")} disabled={!!busy} data-testid="proposal-accept-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-4 font-semibold hover:bg-gold-light transition-colors disabled:opacity-50">
                {busy === "accepted" ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Approve this proposal
              </button>
              <button onClick={() => decide("rejected")} disabled={!!busy} data-testid="proposal-reject-btn"
                className="inline-flex items-center justify-center gap-2 border border-white/25 px-6 py-4 hover:bg-white/5 transition-colors disabled:opacity-50">
                Request changes
              </button>
            </div>
          )}
          <p className="text-center text-gray-500 text-xs mt-4">Questions? Reply to the email we sent, or contact support@asktopfoster.com</p>
        </div>
      </div>
    </div>
  );
}
